import {
  SageJobSiteHoursDaysResponse,
  SageJobSiteHoursDaysSource,
  SageWorkDayHoursSource,
} from "../Interfaces"
import { isISODate, toISODate } from "./isoDate"

export function uniqueSortedDates(dates: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(dates).filter(isISODate))).sort()
}

export function hasHoursOnDay(days: Iterable<string>, iso: string): boolean {
  if (!isISODate(iso)) return false
  if (days instanceof Set) return days.has(iso)
  return Array.from(days).includes(iso)
}

/** Latest non-zero day on or before `requested`, else the most recent day in the set. */
export function snapToHoursDay(days: Iterable<string>, requested: string): string | null {
  const sorted = uniqueSortedDates(days)
  if (sorted.length === 0) return null
  if (isISODate(requested) && sorted.includes(requested)) return requested
  if (isISODate(requested)) {
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      if (sorted[i] <= requested) return sorted[i]
    }
  }
  return sorted[sorted.length - 1]
}

export function previousHoursDay(days: Iterable<string>, current: string): string | null {
  const sorted = uniqueSortedDates(days)
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    if (sorted[i] < current) return sorted[i]
  }
  return null
}

export function nextHoursDay(days: Iterable<string>, current: string): string | null {
  const sorted = uniqueSortedDates(days)
  for (const day of sorted) {
    if (day > current) return day
  }
  return null
}

function asISODate(value: unknown): string | null {
  if (typeof value === "string") {
    const iso = value.slice(0, 10)
    return isISODate(iso) ? iso : null
  }
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return toISODate(value)
  }
  return null
}

function asHours(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

function readDaysArray(raw: Record<string, unknown>): unknown[] {
  for (const key of ["dates", "days", "data"]) {
    const value = raw[key]
    if (Array.isArray(value)) return value
  }
  return []
}

function dayFromEntry(entry: unknown): string | null {
  if (typeof entry === "string") return asISODate(entry)
  if (!entry || typeof entry !== "object") return null
  const row = entry as Record<string, unknown>
  const hours = row.hours == null ? 1 : asHours(row.hours)
  if (hours <= 0) return null
  return asISODate(row.date ?? row.iso ?? row.day)
}

/**
 * Normalize SiteWalkAPI #130:
 * GET /sage-100/job-sites/:id/hours/days?from=&to= → `{ dates: string[] }`
 */
export function normalizeHoursDaysResponse(
  raw: unknown,
  jobSiteId: number,
  from: string,
  to: string,
  source: SageJobSiteHoursDaysSource = "hours-days-api",
): SageJobSiteHoursDaysResponse {
  const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>
  const dates = uniqueSortedDates(readDaysArray(body).map(dayFromEntry).filter((iso): iso is string => !!iso))
    .filter((iso) => (!from || iso >= from) && (!to || iso <= to))

  return {
    success: body.success !== false,
    jobSiteId: asHours(body.jobSiteId) || jobSiteId,
    from: typeof body.from === "string" && isISODate(body.from) ? body.from : from,
    to: typeof body.to === "string" && isISODate(body.to) ? body.to : to,
    dates,
    source,
    error: body.error != null ? String(body.error) : undefined,
  }
}

export function emptyHoursDaysResponse(
  jobSiteId: number,
  from: string,
  to: string,
  source: SageJobSiteHoursDaysSource = "unavailable",
): SageJobSiteHoursDaysResponse {
  return {
    success: true,
    jobSiteId,
    from,
    to,
    dates: [],
    source,
  }
}

function hoursBetween(start?: string | Date | null, end?: string | Date | null, now = Date.now()): number {
  if (!start) return 0
  const startMs = new Date(start).getTime()
  const endMs = end ? new Date(end).getTime() : now
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0
  return (endMs - startMs) / 3_600_000
}

function workDayISODate(workDay: SageWorkDayHoursSource): string | null {
  const fromDate = asISODate(workDay.date)
  if (fromDate) return fromDate
  return asISODate(workDay.clockedInAt)
}

function workDayHours(workDay: SageWorkDayHoursSource, now = Date.now()): number {
  const details = workDay.details || []
  let assigned = 0
  for (const detail of details) {
    if (detail.hours != null && Number.isFinite(Number(detail.hours))) {
      assigned += Number(detail.hours)
      continue
    }
    assigned += hoursBetween(detail.startedAt, detail.endedAt, now)
  }
  const clocked = hoursBetween(workDay.clockedInAt, workDay.clockedOutAt, now)
  return Math.max(assigned, clocked)
}

/** Fallback: unique work-day dates in range whose hours are > 0. */
export function hoursDaysFromWorkDays(
  workDays: SageWorkDayHoursSource[] | undefined,
  jobSiteId: number,
  from: string,
  to: string,
  now = Date.now(),
): SageJobSiteHoursDaysResponse {
  const dates = new Set<string>()
  for (const workDay of workDays || []) {
    if (workDay.sageJobSiteId != null && workDay.sageJobSiteId !== jobSiteId) continue
    if (workDayHours(workDay, now) <= 0) continue
    const iso = workDayISODate(workDay)
    if (!iso) continue
    if ((from && iso < from) || (to && iso > to)) continue
    dates.add(iso)
  }
  return {
    success: true,
    jobSiteId,
    from,
    to,
    dates: uniqueSortedDates(dates),
    source: "work-days-fallback",
  }
}
